<template>
  <div class="container-fluid">
    <page-header
      :page="header.page"
      :actions="header.actions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <div>
      <filter-manager
        v-model="filter.model"
        :filters="filterOptions"
        :display-advanced="false"
        class="filter-manager_extended"
        search-container-class="search-btn"
        @search="search()"
      />

      <custom-separator text="Resultados de Materiales/Equipos" class="my-3" />

      <div v-if="hasResults">
        <result-row
          v-for="item in results"
          :key="item.id"
          :item="item"
          :catalogs="catalogs"
          @center="getMunicipalities"
          @municipality="getSchools"
          @toggle="toggle"
          @selected="searchAll()"
        />

        <pagination
          v-if="pagination.totalItemCount > pagination.pageSize"
          :data="pagination"
          :page-size="pagination.pageSize"
          @pagination-go-page="search"
        />
      </div>

      <custom-result-not-found
        v-else
        text="No hay resultados de Materiales/Equipos"
      />

      <custom-separator class="my-4" text="Materiales/Equipos Seleccionados" />

      <div v-if="hasSelectedResults">
        <result-row-selected
          v-for="item in selectedResults"
          :key="item.id"
          :item="item"
          @removed="searchAll()"
        />

        <pagination
          v-if="
            paginationForSelecteds.totalItemCount >
              paginationForSelecteds.pageSize
          "
          :data="paginationForSelecteds"
          :page-size="paginationForSelecteds.pageSize"
          @pagination-go-page="searchSelected"
        />
      </div>

      <custom-result-not-found
        v-else
        text="No hay Materiales/Equipos seleccionados"
      />
    </div>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';

import ResultRow from './components/ResultRow.vue';
import ResultRowSelected from './components/ResultRowSelected.vue';

import storeMixin from '@/mixin/storeGenerator';
import alertMixin from '@/mixin/alert.mixin';
import { name as appModuleName } from '@/modules/app/store/index';
import { mapGetters } from 'vuex';

import inventoryCatalogService from '@/services/api/inventorycatalog.api';
import materialsAndEquipmentsService from '@/services/api/materialsAndEquipments.api';
import inventoryOrderMaterialsAndEquipmentsService from '@/services/api/inventoryOrderMaterialsAndEquipments.api';
import locationService from '@/services/api/location.api';
import resourcesService from '@/services/resources.js';
import materialsService from '@/services/api/materialsEquipment.api.js';

const DEFAULT_PAGE_SIZE = 5;

export default {
  name: 'MaterialsAndEquipmentsRequestsManage',
  components: {
    PageHeader,
    ActionButton,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
    ResultRowSelected,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    results: [],
    selectedResults: [],
    filter: {
      model: {
        catalog: null,
        category: null,
      },
      options: {
        catalogs: [],
        categories: [],
      },
    },
    pagination: {
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    paginationForSelecteds: {
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    catalogs: {
      reasons: [],
      saloonTypes: [],
      centers: [],
      municipalities: [],
      schools: [],
      resources: [],
      documentTypes: [],
    },
  }),
  computed: {
    ...mapGetters(appModuleName, {
      isMobile: 'getIsMobileDensity',
    }),
    header() {
      return {
        page: {
          icon: 'fas fa-book-open',
          title: 'Crear Solicitud',
          routes: [
            'Inicio',
            'PEI',
            'Solicitudes de Material/Equipo',
            'Crear Solicitud',
          ],
        },
        actions: [
          {
            text: 'Regresar',
            icon: 'fas fa-arrow-circle-left',
            variant: 'outline-success',
            click: () => this.$router.back(),
          },
          {
            text: 'Someter',
            icon: 'fas fa-flag',
            variant: 'success',
            click: () => this.submit(),
          },
        ],
      };
    },
    filterOptions() {
      return [
        {
          name: 'catalog',
          component: 'CustomDropDown',
          options: this.filter.options.catalogs,
          value: this.filter.model.catalog,
          placeholder: 'Título',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'category',
          component: 'CustomDropDown',
          options: this.filter.options.categories,
          value: this.filter.model.category,
          placeholder: 'Categoría',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
      ];
    },
    hasResults() {
      return Boolean(this.results.length);
    },
    hasSelectedResults() {
      return Boolean(this.selectedResults.length);
    },
    academicYearId() {
      return this.currentAcademicYear().id;
    },
  },
  async mounted() {
    await Promise.allSettled([
      this.getCategories(),
      this.getCatalogs(),
      this.getReasons(),
      this.getSaloonTypes(),
      this.getCenters(),
      this.getResources(),
      this.getDocumentTypes(),
      this.searchAll(),
    ]);
  },
  methods: {
    toggle(item) {
      if (item.selected) {
        this.results
          .filter((x) => x.id !== item.id)
          .forEach((x) => {
            x.selected = false;
            x.disabled = true;
          });
      } else {
        this.results.forEach((x) => {
          x.selected = false;
          x.disabled = false;
        });
      }
    },
    async searchAll() {
      await Promise.all([this.search(), this.searchSelected()]);
    },
    async search(currentPage = 1) {
      const { catalog, category } = this.filter.model;

      const { pageSize } = this.pagination;

      const {
        data,
      } = await inventoryOrderMaterialsAndEquipmentsService.searchForAdd({
        catalogId: catalog?.id,
        categoryId: category?.id,
        currentPage,
        pageSize,
      });

      this.results = data.data.map((x) => ({
        ...x,
        selected: false,
        disabled: false,
      }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async searchSelected(currentPage = 1) {
      const { catalog, category } = this.filter.model;

      const { pageSize } = this.pagination;

      const {
        data,
      } = await inventoryOrderMaterialsAndEquipmentsService.searchForSelectedAdd(
        {
          catalogId: catalog?.id,
          categoryId: category?.id,
          currentPage,
          pageSize,
        }
      );

      this.selectedResults = data.data;

      this.paginationForSelecteds = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async getCategories() {
      const { data } = await materialsAndEquipmentsService.getCategories();
      this.filter.options.categories = data;
    },
    async getCatalogs() {
      const { data } = await inventoryCatalogService.getKeyValue({ type: 2 });
      this.filter.options.catalogs = data;
    },
    async getReasons() {
      const {
        data,
      } = await inventoryOrderMaterialsAndEquipmentsService.getReasons();
      this.catalogs.reasons = data;
    },
    async getSaloonTypes() {
      const { data } = await materialsAndEquipmentsService.getSaloon();
      this.catalogs.saloonTypes = data;
    },
    async getCenters() {
      const { data } = await locationService.getCenters({
        academicYearId: this.academicYearId,
      });
      this.catalogs.centers = data;
    },
    async getMunicipalities(centerId) {
      this.catalogs.municipalities = [];
      this.catalogs.schools = [];

      if (!centerId) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId,
      });
      this.catalogs.municipalities = data;
    },
    async getSchools(centerId, municipalityId) {
      this.catalogs.schools = [];

      if (!municipalityId) return;

      const { data } = await locationService.getSchools({
        academicYearId: this.academicYearId,
        centerId,
        municipalityId,
      });
      this.catalogs.schools = data;
    },
    async getResources() {
      this.catalogs.resources = await resourcesService.LoadValidationMessages([
        '44',
      ]);
    },
    async getDocumentTypes() {
      this.catalogs.documentTypes = (
        await materialsService.getDocumentTypes()
      ).data;
    },
    async submit() {
      if (!this.selectedResults.length) {
        this.ShowToast(
          'Error',
          'Debe seleccionar al menos un material/equipo para someter',
          'error'
        );
        return;
      }

      const confirmed = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea someter los materiales/equipos seleccionados?',
        'Si',
        'No'
      );
      if (!confirmed) return;

      const { orderId } = this.selectedResults[0];

      const { data } = await inventoryOrderMaterialsAndEquipmentsService
        .submit(orderId)
        .catch((e) => {
          this.ShowToast({
            message: e.response.data.message,
            variant: 'danger',
          });
        });

      if (data) {
        this.ShowToast(
          'Éxito',
          'La solicitud se ha sometido correctamente.',
          'success'
        );
        this.$router.go(-1);
      }
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const routes = [];
    next(async (vm) => {
      if (routes.includes(from.name)) {
        vm.recoveryData();
        await vm.search(1);
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
};
</script>
<style scoped lang="scss">
::v-deep .font-weight-bold {
  font-weight: 700 !important;
}

.actions-dropdown ::v-deep #dropdownMenuButton {
  color: #11291b !important;
}
</style>
